/* =============================================================================
   hub-exhibition.js — EXHIBITION MODE, the payment backstop
   =============================================================================
   Loaded on every page, synchronously, from layout/theme.liquid. Exits on the
   first line unless Theme settings → Exhibition mode is ticked.

   WHY THIS EXISTS SEPARATELY FROM THE LIQUID CHANGES
   sections/main-product.liquid, sections/main-cart.liquid and
   snippets/hub-cart-drawer.liquid already replace the buy controls this theme
   renders itself. That is the part a visitor sees, and it is the part that has
   to look deliberate. This file is the part that has to be RIGHT: it closes the
   paths the markup cannot reach.

     - assets/hub-booking.js POSTs /cart/add.js directly and then sends the
       visitor to /checkout. So does hub-athlete-card.js, hub-effects.js and
       hub-shop.js. Editing five call sites to ask a flag would leave a sixth.
     - The Easify product-options app renders its own controls into the product
       page. They are not in this theme's markup and cannot be edited from it.
     - Shopify's own dynamic checkout button (Shop Pay / Apple Pay / Google Pay)
       is injected by the platform and skips the cart entirely.
     - A page cached before the switch was flipped still has live buy buttons in
       it. Its HTML is stale; this script is not, because it is re-fetched with
       the asset URL.

   So the rule is enforced at the network boundary — /cart/add and /checkout are
   refused no matter who asks — and the visible controls are relabelled on top of
   that. Belt and braces, because the failure mode being prevented is TAKING
   MONEY FOR SOMETHING NOBODY CAN DELIVER while the partner contracts are still
   unsigned.

   NOT A SUBSTITUTE FOR TURNING PAYMENTS OFF IN ADMIN. Everything here is
   client-side. Someone with a terminal can still POST to /cart/add and open
   /checkout, because those endpoints belong to Shopify and the theme cannot
   refuse them. The only hard stop is deactivating the payment providers in
   Shopify Admin → Settings → Payments; see HANDOVER for that click-path. This
   file makes it impossible to pay BY ACCIDENT, which is the actual risk on a
   soft-launch weekend.

   Remove this file + its <script> tag in theme.liquid to retire.
   ========================================================================== */
(function () {
  'use strict';

  var CFG = window.ATHLO_EXH || {};
  if (!CFG.off) return;

  var NOTICE = CFG.notice || 'Preview only — not on sale yet';
  var BLURB = CFG.blurb || '';

  /* --- what counts as a money path ---------------------------------------
     Matched on the PATH only. Query strings and absolute URLs to the shop's own
     origin both normalise down to this, and a foreign origin never matches —
     blocking an analytics beacon that happens to contain the word "checkout"
     would be a bug, not extra safety. */
  var MONEY = /\/(cart\/add|checkout|cart\/checkout)(\.js|\.json)?(\/|\?|$)/i;

  function isMoneyUrl(u) {
    if (!u) return false;
    try {
      var url = new URL(String(u), window.location.origin);
      if (url.origin !== window.location.origin) return false;
      return MONEY.test(url.pathname + (url.pathname.slice(-1) === '/' ? '' : '/'));
    } catch (e) {
      return MONEY.test(String(u));
    }
  }

  /* --- the toast ----------------------------------------------------------
     One node, reused. Announced politely so a screen-reader user is told the
     button did nothing on purpose rather than being left with silence. */
  var toastEl = null;
  var toastTimer = null;
  function toast(msg) {
    try {
      if (!toastEl) {
        toastEl = document.createElement('div');
        toastEl.className = 'exh-toast';
        toastEl.setAttribute('role', 'status');
        toastEl.setAttribute('aria-live', 'polite');
        document.body.appendChild(toastEl);
      }
      toastEl.textContent = msg || NOTICE;
      toastEl.classList.add('is-on');
      clearTimeout(toastTimer);
      toastTimer = setTimeout(function () { toastEl.classList.remove('is-on'); }, 4000);
    } catch (e) {}
  }

  /* --- network boundary ---------------------------------------------------
     A blocked /cart/add answers in Shopify's OWN error shape (422 + status /
     message / description). Every cart caller in this theme already knows how to
     read that, so they surface the notice through their existing error path
     instead of hanging on a promise that never settles. */
  function refusal() {
    return {
      status: 422,
      message: NOTICE,
      description: BLURB || NOTICE
    };
  }

  var _fetch = window.fetch;
  if (typeof _fetch === 'function') {
    window.fetch = function (input, init) {
      var url = (input && typeof input === 'object' && 'url' in input) ? input.url : input;
      if (isMoneyUrl(url)) {
        toast(NOTICE);
        var body = JSON.stringify(refusal());
        if (typeof Response === 'function') {
          return Promise.resolve(new Response(body, {
            status: 422,
            headers: { 'Content-Type': 'application/json' }
          }));
        }
        return Promise.reject(new Error(NOTICE));
      }
      return _fetch.apply(this, arguments);
    };
  }

  var _open = window.XMLHttpRequest && window.XMLHttpRequest.prototype.open;
  var _send = window.XMLHttpRequest && window.XMLHttpRequest.prototype.send;
  if (_open && _send) {
    window.XMLHttpRequest.prototype.open = function (method, url) {
      this.__exhBlocked = isMoneyUrl(url);
      return _open.apply(this, arguments);
    };
    window.XMLHttpRequest.prototype.send = function () {
      if (this.__exhBlocked) {
        toast(NOTICE);
        try { this.abort(); } catch (e) {}
        return;
      }
      return _send.apply(this, arguments);
    };
  }

  /* --- form submits and clicks -------------------------------------------
     Capture phase on both, so this runs before any handler the theme or an app
     attached — a listener that has already called preventDefault and started its
     own fetch is caught by the network patch above, but one that lets the native
     submit through has to be stopped here. */
  document.addEventListener('submit', function (e) {
    var f = e.target;
    if (!f || f.tagName !== 'FORM') return;
    var action = f.getAttribute('action') || '';
    var isCartForm = isMoneyUrl(action) || /\/cart\/add/i.test(action);
    // A product form with no action attribute still posts to /cart/add.
    if (!isCartForm && f.querySelector('[name="add"]')) isCartForm = true;
    if (!isCartForm && f.querySelector('[name="checkout"]')) isCartForm = true;
    if (!isCartForm) return;
    e.preventDefault();
    e.stopPropagation();
    toast(NOTICE);
  }, true);

  document.addEventListener('click', function (e) {
    var t = e.target;
    if (!t || !t.closest) return;
    var hit = t.closest('a[href], button, [data-sticky-add], .shopify-payment-button');
    if (!hit) return;

    var isMoney = false;
    if (hit.tagName === 'A') {
      isMoney = isMoneyUrl(hit.getAttribute('href'));
    } else if (hit.name === 'add' || hit.name === 'checkout') {
      isMoney = true;
    } else if (hit.hasAttribute && hit.hasAttribute('data-sticky-add')) {
      isMoney = true;
    } else if (hit.closest('.shopify-payment-button')) {
      isMoney = true;
    }
    if (!isMoney) return;
    e.preventDefault();
    e.stopPropagation();
    toast(NOTICE);
  }, true);

  /* --- relabel whatever is left ------------------------------------------
     The Liquid templates relabel the buttons they own. This catches the ones
     rendered by JS after load (quick-add tiles, the booking picker's confirm,
     app-injected controls) so a visitor is never left looking at a live-looking
     "Add to cart" that silently does nothing. */
  var SELL_SEL = [
    'button[name="add"]',
    'button[name="checkout"]',
    '[data-sticky-add]',
    '[data-quick-add]',
    '.shopify-payment-button',
    '[data-booking-confirm]',
    '[data-booking-checkout]'
  ].join(',');

  function relabel(root) {
    var nodes;
    try { nodes = (root || document).querySelectorAll(SELL_SEL); } catch (e) { return; }
    Array.prototype.forEach.call(nodes, function (el) {
      if (el.__exhDone) return;
      el.__exhDone = true;
      if (el.classList && el.classList.contains('shopify-payment-button')) {
        el.setAttribute('hidden', 'hidden');
        el.style.display = 'none';
        return;
      }
      el.setAttribute('disabled', 'disabled');
      el.setAttribute('aria-disabled', 'true');
      el.classList.add('exh-dead');
      var label = el.querySelector('span') || el;
      label.textContent = NOTICE;
    });
  }

  function boot() {
    relabel(document);
    try {
      new MutationObserver(function (muts) {
        for (var i = 0; i < muts.length; i++) {
          var added = muts[i].addedNodes;
          for (var j = 0; j < added.length; j++) {
            if (added[j].nodeType === 1) relabel(added[j].parentNode || document);
          }
        }
      }).observe(document.documentElement, { childList: true, subtree: true });
    } catch (e) {}
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
